import { queryClient } from './query-client';
import { clearPersistedQueryCache } from './query-persister';
import { wipeUserScopedKeys } from './user-scoped-storage';
import {
  forgetActiveProjectIfMatches,
  resetActiveProjectStore,
} from './active-project-store';
import { seenFactsBatcher } from '@/features/design/render/api/seen-facts-batcher';
import { markBuildingDeleted } from './deleted-buildings';

/**
 * TanStack Query cache invalidation service
 *
 * Provides centralized cache invalidation strategies for all entity operations.
 * Used to keep the cache in sync with the backend after mutations.
 *
 * ✅ USAGE:
 * - Projects: afterCreateProject, afterUpdateProject, afterDeleteProject
 * - Concepts: afterCreateConcept, afterUpdateConcept, afterDeleteConcept
 * - Angles: afterCreateProjectAngle, afterUpdateAngle, afterDeleteAngle
 * - Subscriptions: afterSubscriptionChange
 * - Auth: afterLogin, afterLogout
 *
 * ℹ️ NOTES:
 * - Render data is managed by JobsStore (Zustand), not TanStack Query
 * - WebSocket events trigger JobsStore updates directly
 * - staleTime is 30 seconds as a safety net if invalidation is missed
 */

// Single source of truth for every TanStack Query `queryKey` in
// `apps/web`. Shapes intentionally use `[entity, op, id]` ordering so that
// `invalidateQueries({ queryKey: [entity, op] })` sweeps every per-id
// query — and so the WS-mutation lock factory in `query-client.ts` can
// derive the entity directly from `queryKey[0]`.
//
// Adding a new query? Put it here. The acceptance grep
// `grep -rn "queryKey: \['" src` must remain limited to this file (W3.8).

interface ProjectListParams {
  sort?: string;
  filter?: Record<string, unknown>;
  enabled?: boolean;
}

export const cacheKeys = {
  // Projects
  projects: {
    all: ['projects'] as const,
    lists: () => ['projects', 'list'] as const,
    list: (params?: ProjectListParams) => ['projects', 'list', params] as const,
    details: () => ['projects', 'detail'] as const,
    detail: (projectId: string) => ['projects', 'detail', projectId] as const,
    concepts: (projectId: string) =>
      ['projects', 'concepts', projectId] as const,
  },

  // Concepts
  concepts: {
    all: ['concepts'] as const,
    lists: () => ['concepts', 'list'] as const,
    list: (projectId?: string) => ['concepts', 'list', projectId] as const,
    details: () => ['concepts', 'detail'] as const,
    detail: (conceptId: string) => ['concepts', 'detail', conceptId] as const,
    angles: (conceptId: string | undefined) =>
      ['concepts', 'angles', conceptId] as const,
    overview: (projectId: string, conceptId: string) =>
      ['concept-overview-details', projectId, conceptId] as const,
  },

  // Renders
  renders: {
    all: ['renders'] as const,
    list: (conceptId?: string) => ['renders', 'list', conceptId] as const,
    detail: (renderId: string) => ['renders', 'detail', renderId] as const,
    single: (renderId: string) => ['render', renderId] as const,
    active: ['renders', 'active'] as const,
    queue: ['renders', 'queue'] as const,
    history: (conceptId?: string) => ['renders', 'history', conceptId] as const,
  },

  // Angles
  angles: {
    all: ['angles'] as const,
    list: (conceptId?: string) => ['angles', 'list', conceptId] as const,
    detail: (angleId: string) => ['angles', 'detail', angleId] as const,
  },

  // Concept-angle join records (mirror of WS broadcast topology)
  conceptAngles: {
    all: ['concept-angles'] as const,
  },

  // Project-angles list (project-scoped pool of available angles)
  projectAngles: {
    all: ['project-angles'] as const,
    list: (projectId: string) => ['project-angles', 'list', projectId] as const,
  },

  // History (concept-level)
  history: {
    all: ['history'] as const,
    lists: () => ['history', 'list'] as const,
    list: (filters?: unknown, sort?: string) =>
      ['history', 'list', { filters, sort }] as const,
    infinite: (filters?: unknown, sort?: string) =>
      ['history', 'infinite', { filters, sort }] as const,
  },

  // Trash (soft-deleted buildings + designs still inside the restore window)
  trash: {
    all: ['trash'] as const,
    infinite: (subtype?: string) => ['trash', 'infinite', { subtype }] as const,
  },

  // Render history (per-render history list)
  renderHistory: {
    all: ['renderHistory'] as const,
    lists: () => ['renderHistory', 'list'] as const,
    list: (filters?: unknown, sort?: string) =>
      ['renderHistory', 'list', { filters, sort }] as const,
    infinite: (filters?: unknown, sort?: string) =>
      ['renderHistory', 'infinite', { filters, sort }] as const,
  },

  // Job history (renders + albums combined feed)
  jobHistory: {
    all: ['jobHistory'] as const,
    lists: () => ['jobHistory', 'list'] as const,
    list: (filters?: unknown, sort?: string) =>
      ['jobHistory', 'list', { filters, sort }] as const,
    infinite: (filters?: unknown, sort?: string) =>
      ['jobHistory', 'infinite', { filters, sort }] as const,
  },

  // Job queue (active background jobs)
  jobQueue: {
    all: ['job-queue'] as const,
    list: (limit?: number) => ['job-queue', limit] as const,
  },

  // Building-scoped render START gate (GET /v1/queue/status?project_id=).
  // Separate from the account-global WS queue aggregate: `block_reason` is
  // per-building (a Facade Kit lifts the free/token gate on its building only),
  // so the render screen reads this keyed by its building instead of the
  // user-level snapshot.
  queueStatus: {
    all: ['queue-status'] as const,
    building: (projectId: string) => ['queue-status', projectId] as const,
  },

  // Auth (user / session)
  auth: {
    user: ['auth', 'user'] as const,
    session: ['auth', 'session'] as const,
  },

  // Profile + raw user + settings (legacy invalidation roots — no current
  // readers in features, but WS handlers fan out to these for forward
  // compatibility with future profile/settings queries).
  profile: ['profile'] as const,
  user: ['user'] as const,
  settings: ['settings'] as const,

  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
    // Bundle the user's effective subscription state — subscription resource,
    // resolved plan resource (real or guest), usage, isGuestMode, effective
    // plan limits. Single key so WS pushes and auth seeders both write
    // through one channel (STATE_POLICY).
    active: ['subscriptions', 'active'] as const,
    current: ['subscriptions', 'current'] as const,
    list: ['subscriptions', 'list'] as const,
    plans: ['subscriptions', 'plans'] as const,
    usage: ['subscriptions', 'usage'] as const,
  },

  // Storefront catalogs (server-driven copy/price + per-user ownership flags,
  // locale — and for the store catalog, projectId — keyed). Each entry caches
  // for 5min, so a purchase must invalidate by the `.all` prefix: a just-bought
  // kit/pass/token otherwise keeps showing stale `has_active_pack` /
  // `is_additional` / `is_owned` / price until the staleTime lapses. The prefix
  // sweeps every locale/project variant in one call.
  storeCatalog: {
    all: ['store-catalog'] as const,
  },
  tokenPackages: {
    all: ['token-packages'] as const,
  },
  passes: {
    all: ['passes'] as const,
  },

  // Payments
  payments: ['payments'] as const,

  // Token balance — server-owned scalar, WS pushes via setQueryData.
  // Lives at the top level (not under subscriptions) because guests also
  // hold a balance and the WS event topology is per-user, not per-plan.
  balance: ['balance'] as const,

  // First-run wizard state from the profile payload. Seeded by
  // `applyProfileData`, never fetched on its own.
  firstRun: ['first-run'] as const,
  /** `attributes.sign_in` of the profile (ADR-001 §5). */
  signIn: ['sign-in'] as const,

  // Palettes
  palettes: {
    all: ['palettes'] as const,
  },

  // The catalogue of real manufacturer products — every library at once. It is
  // static reference data (a product's name, code and colour value never change
  // under us), so lists are cached hard and keyed by the drill-down that
  // produced them: category → brand → line → axis value, plus the search term.
  catalog: {
    all: ['catalog'] as const,
    categories: () => ['catalog', 'categories'] as const,
    brands: (category: string, country: string | null = null) =>
      ['catalog', 'brands', category, country] as const,
    lines: (category: string, brandId: string | null) =>
      ['catalog', 'lines', category, brandId] as const,
    products: (
      category: string,
      search: string,
      brandId: string | null,
      lineId: string | null,
      axisValue: string | null,
      colorFamily: string | null
    ) =>
      [
        'catalog',
        'products',
        category,
        search,
        brandId,
        lineId,
        axisValue,
        colorFamily,
      ] as const,
  },

  // Resolved Brands picks (labels for the settings rows). Keyed by the token
  // list itself — the catalogue behind it is static reference data, same as
  // the product lists above.
  brandSelections: {
    all: ['brand-selections'] as const,
    resolve: (tokens: string[]) =>
      ['brand-selections', 'resolve', tokens.join(',')] as const,
  },

  // Project references (project-level uploaded reference assets)
  references: {
    all: ['project-references'] as const,
    lists: () => ['project-references', 'list'] as const,
    // `section` (brief | reference | undefined=all) is a trailing key so that
    // invalidating the `listsByProject` prefix sweeps every section cache.
    list: (projectId: string, section?: 'brief' | 'reference') =>
      ['project-references', 'list', projectId, section] as const,
    // Prefix matching every section list for a project (all | brief |
    // reference). Use for mutation/WS invalidations so a change to one
    // reference refreshes whichever section views are mounted.
    listsByProject: (projectId: string) =>
      ['project-references', 'list', projectId] as const,
    byCategory: (projectId: string, category: string) =>
      ['project-references', 'list', projectId, 'category', category] as const,
    details: () => ['project-references', 'detail'] as const,
    detail: (projectId: string, referenceId: string) =>
      ['project-references', 'detail', projectId, referenceId] as const,
  },

  // Controls (per-angle structure sketches)
  controls: {
    all: ['controls'] as const,
    lists: () => ['controls', 'list'] as const,
    listByAngle: (angleId: string) =>
      ['controls', 'list', 'angle', angleId] as const,
    listByProject: (projectId: string) =>
      ['controls', 'list', 'project', projectId] as const,
    details: () => ['controls', 'detail'] as const,
    detail: (controlId: string) => ['controls', 'detail', controlId] as const,
  },

  // Albums (per-concept generated PDFs)
  albums: {
    all: ['albums'] as const,
    list: (conceptId: string) => ['albums', conceptId] as const,
    // Album-tab intro (empty state): server-driven benefits copy. Locale-keyed
    // — the copy is localized server-side, so a locale switch refetches it.
    intro: (conceptId: string, locale: string) =>
      ['album-intro', conceptId, locale] as const,
  },

  // Album generation cost prediction — server-computed token price shown on
  // the Generate button. The hook appends conceptId + the debounced params
  // object, so every settings combination is its own cache entry.
  albumCostPrediction: {
    all: ['album-cost-prediction'] as const,
  },

  // Cost estimates — concept-scoped list, project-scoped list (FE3 page) +
  // per-estimate detail (with items).
  estimates: {
    all: ['estimates'] as const,
    list: (conceptId: string) => ['estimates', 'list', conceptId] as const,
    projectList: (projectId: string) =>
      ['estimates', 'project-list', projectId] as const,
    detail: (estimateId: string) =>
      ['estimates', 'detail', estimateId] as const,
    // Estimate-tab intro (empty state): region/units derive from project
    // goals, so a project PATCH must invalidate it by this project prefix.
    intro: (projectId: string, conceptId: string, locale: string) =>
      ['estimate-intro', projectId, conceptId, locale] as const,
    introByProject: (projectId: string) =>
      ['estimate-intro', projectId] as const,
    // Design lines (ADR-001): a design's estimate/album context, the
    // building's lines and each line's version history. Nested under
    // `estimates` so missed `estimates`/`albums` WS events (L2) refetch them too.
    designLines: ['estimates', 'design-lines'] as const,
    designLineContext: (conceptId: string) =>
      ['estimates', 'design-lines', 'context', conceptId] as const,
    projectDesignLines: (projectId: string) =>
      ['estimates', 'design-lines', 'project', projectId] as const,
    lineEstimateVersions: (lineId: string) =>
      ['estimates', 'design-lines', 'versions', lineId] as const,
    lineAlbumVersions: (lineId: string) =>
      ['estimates', 'design-lines', 'albums', lineId] as const,
  },

  // Supported display currencies (GET /v1/currencies) — static catalog,
  // cached long (24h) by the consumer hook.
  currencies: ['currencies'] as const,

  // Project albums aggregations (project-level dashboards)
  projectAlbums: {
    all: ['project-albums'] as const,
    list: (projectId: string) => ['project-albums', projectId] as const,
    batch: (projectId: string) => ['project-albums-batch', projectId] as const,
  },

  // Onboarding slides (locale-scoped)
  onboardingSlides: (locale: string) =>
    ['onboarding-slides', locale] as const,

  // Help center tree (locale-scoped)
  helpCenter: (locale: string) => ['help-center', locale] as const,

  // Render loading-screen facts (project + locale)
  loadingFacts: {
    byProjectAndLocale: (projectId: string | undefined, locale: string) =>
      ['loading-facts', projectId, locale] as const,
    byProject: (projectId: string) => ['loading-facts', projectId] as const,
  },

  // AI architect (docs/assistant/ADR-001)
  assistant: {
    thread: (scope: string) => ['assistant', 'thread', scope] as const,
    messages: (threadId: string) =>
      ['assistant', 'messages', threadId] as const,
    /** "Chat history" of a thread, re-read each time it opens. */
    history: (threadId: string) => ['assistant', 'history', threadId] as const,
    moments: () => ['assistant', 'moments'] as const,
    moment: (buildingId: string) =>
      ['assistant', 'moments', buildingId] as const,
  },
} as const;

/**
 * Invalidation strategies for different entity operations
 */
export const cacheInvalidation = {
  // Project operations
  afterCreateProject: () => {
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
  },

  afterUpdateProject: (projectId: string) => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.list() });
    // The estimate intro's region_label/measurement_system are computed from
    // the project's construction_region — a project PATCH may change them.
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.introByProject(projectId),
    });
  },

  afterDeleteProject: (projectId: string) => {
    // Remember the deletion first: the building page may still be mounted, and
    // removing an observed query below makes it refetch a row that is gone.
    // See `deleted-buildings` — without this the 404 paints over the page.
    markBuildingDeleted(projectId);
    queryClient.cancelQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
    // Remove specific project data from cache
    queryClient.removeQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
    queryClient.removeQueries({
      queryKey: cacheKeys.projects.concepts(projectId),
    });
    // Invalidate lists to reflect deletion
    // Note: Don't invalidate .all as it triggers unnecessary refetches
    // Only invalidate list queries
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.list() });

    // Drop the in-memory active-building pointer if it points at the deleted
    // building (authoritative source — see active-project-store), then clear the
    // best-effort localStorage mirror below.
    forgetActiveProjectIfMatches(projectId);

    // Clear lastOpenedProjectId if it matches the deleted project. Keys are
    // `${userId}:facade:lastOpenedProjectId` (see `useLastProject`), so we
    // scan for the suffix instead of the leading prefix.
    try {
      const suffix = ':facade:lastOpenedProjectId';
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.endsWith(suffix) || key === 'facade:lastOpenedProjectId') {
          const stored = window.localStorage.getItem(key);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.projectId === projectId) {
              window.localStorage.removeItem(key);
              window.dispatchEvent(new Event('local-storage'));
            }
          }
        }
      }
    } catch {
      /* ignore parse errors */
    }
  },

  // Concept operations
  afterCreateConcept: (projectId: string) => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.list(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projects.concepts(projectId),
    });
  },

  afterUpdateConcept: (conceptId: string, projectId?: string) => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.detail(conceptId),
    });
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(projectId),
      });
    }
  },

  afterDeleteConcept: (conceptId: string, projectId?: string) => {
    queryClient.removeQueries({
      queryKey: cacheKeys.concepts.detail(conceptId),
    });
    queryClient.removeQueries({
      queryKey: cacheKeys.concepts.angles(conceptId),
    });
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.concepts(projectId),
      });
    }
  },

  // Angle operations
  afterCreateProjectAngle: (projectId: string) => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAngles.list(projectId),
    });
  },

  afterUpdateAngle: (angleId: string, conceptId?: string) => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.angles.detail(angleId),
    });
    if (conceptId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.angles.list(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.angles(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.detail(conceptId),
      });
    }
  },

  afterDeleteAngle: (angleId: string, conceptId?: string) => {
    queryClient.removeQueries({ queryKey: cacheKeys.angles.detail(angleId) });
    if (conceptId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.angles.list(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.angles(conceptId),
      });
      // Invalidate concept detail to update settings and angles list
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.detail(conceptId),
      });
    }
  },

  // WebSocket-triggered concept change (covers create/update/delete from any source)
  afterConceptChangedViaWebSocket: (projectId?: string) => {
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.concepts.list(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: cacheKeys.concepts.all });
    }
  },

  // WebSocket-triggered album change
  afterAlbumChangedViaWebSocket: (conceptId?: string, projectId?: string) => {
    if (conceptId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.albums.list(conceptId),
      });
    }
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projectAlbums.list(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projectAlbums.batch(projectId),
      });
    }
  },

  // Subscription updates
  afterSubscriptionChange: () => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.subscriptions.current,
    });
    queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.list });
    queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.usage });
    queryClient.invalidateQueries({ queryKey: cacheKeys.auth.user });
    queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  },

  // Auth updates
  afterLogin: () => {
    // Invalidate all queries to refresh with new auth state
    queryClient.invalidateQueries();
  },

  afterLogout: async (previousUserId?: string | null) => {
    // Drop in-memory cache first so any in-flight renders don't re-persist
    // the previous user's data into IDB after we wipe it.
    queryClient.clear();
    // Scope the IndexedDB wipe to the account that just logged out — other
    // accounts keep their own per-user buckets (they can't leak across users).
    await clearPersistedQueryCache(previousUserId ?? null);
    // Wipe localStorage entries scoped to the previous user — render drafts,
    // album/feedback/preferences Zustand persist buckets. Otherwise the next
    // account on this device sees the prior user's settings.
    wipeUserScopedKeys(previousUserId);
    // Reset the in-memory active-building pointer so the next account on this
    // device doesn't inherit the previous user's building.
    resetActiveProjectStore();
    // Drop pending loading-fact "viewed" ids — otherwise the next user's
    // first POST /loading-facts/seen leaks the prior user's fact ids.
    seenFactsBatcher.clear();
  },
};

/**
 * Optimistic update helpers
 */
export const optimisticUpdates = {
  updateProject: (projectId: string, updater: (old: unknown) => unknown) => {
    queryClient.setQueryData(cacheKeys.projects.detail(projectId), updater);
  },

  updateConcept: (conceptId: string, updater: (old: unknown) => unknown) => {
    queryClient.setQueryData(cacheKeys.concepts.detail(conceptId), updater);
  },

  updateRender: (renderId: string, updater: (old: unknown) => unknown) => {
    queryClient.setQueryData(cacheKeys.renders.detail(renderId), updater);
  },
};

/**
 * Prefetch helpers for better UX
 */
export const prefetchStrategies = {
  prefetchProjectDetails: async (
    projectId: string,
    fetcher: () => Promise<unknown>
  ) => {
    await queryClient.prefetchQuery({
      queryKey: cacheKeys.projects.detail(projectId),
      queryFn: fetcher,
    });
  },

  prefetchConceptDetails: async (
    conceptId: string,
    fetcher: () => Promise<unknown>
  ) => {
    await queryClient.prefetchQuery({
      queryKey: cacheKeys.concepts.detail(conceptId),
      queryFn: fetcher,
    });
  },
};
